import { virtualize } from "@lit-labs/virtualizer/virtualize.js";
import {
    type BonusesBalance,
    type DateRange,
    type EmployeeSortProperty,
    GetBalancesParams,
    employeeNameOrderForSort,
    formatDate,
    formatNumber,
    formatRange,
    getEmployeeName,
    getRange,
    isAbortError,
    serializeColumOptions,
    sumBonusesBalances,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { popover } from "../../../directives/popover";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import "../../../elements/popover";
import { app } from "../../../init";
import { LatestRequest } from "../../../lib/latest-request";
import { singleton } from "../../../lib/singleton";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { VirtualizerMixin } from "../../../mixins/virtualizer";
import { shared } from "../../../styles";
import "../components/recalcuate-results-dialog";
import type { RecalculateResultsDialog } from "../components/recalcuate-results-dialog";
import "../components/reset-balances-dialog";
import type { ResetBalancesDialog } from "../components/reset-balances-dialog";
import "../employees-filter";

@customElement("ptc-employees-bonuses-balance-all")
export class EmployeesBonusesBalanceAll extends VirtualizerMixin(Routing(StateMixin(LitElement))) {
    routePattern = /^employees\/all\/bonuses-balance/;

    get routeTitle() {
        return "Zuschlagskonten: Alle Mitarbeiter";
    }

    @state()
    private _balances: BonusesBalance[] = [];

    @state()
    private _loading = false;

    private _request = new LatestRequest(this);

    @singleton("ptc-reset-balances-dialog")
    private _resetBalancesDialog: ResetBalancesDialog;

    @singleton("ptc-recalculate-results-dialog")
    private _recalculateResultsDialog: RecalculateResultsDialog;

    defaultRange = () => getRange(new Date(), "month");

    rangeChanged = () => this.load();

    async load() {
        if (!this.dateRange) {
            return;
        }

        this._loading = true;

        try {
            this._balances = await app.api.getBonusesBalances(
                new GetBalancesParams({
                    from: this.dateRange.from,
                    to: this.dateRange.to,
                    resolution: "none",
                }),
                { signal: this._request.start() }
            );
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _resetBalances() {
        const didChange = await this._resetBalancesDialog.show({
            date: this.dateFrom,
            filters: app.employeeFilters.filters,
        });
        if (didChange) {
            // update in the background
            void this.load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            table {
                border-collapse: separate;
                display: block;
            }

            thead {
                display: block;
                position: sticky;
                top: 0;
                z-index: 2;
            }

            tbody {
                display: block;
                margin-bottom: 1em;
                box-sizing: border-box;
            }

            tr {
                display: grid;
                grid-template-columns: var(--column-width-employee) 10em repeat(4, 8em);
                align-items: center;
                width: 100%;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
            }

            td {
                text-align: right;
            }

            thead {
                font-weight: bold;
                background: var(--color-bg);
                text-align: center;
            }

            tbody.total {
                font-weight: bold;
                box-shadow: rgb(0 0 0 / 10%) 0 1px 0;
                position: sticky;
                top: 2.3em;
                z-index: 2;
                background: var(--color-bg);
            }
        `,
    ];

    private _renderBalances() {
        const filteredEmployees = app.getFilteredEmployees(this.dateRange ?? undefined);
        const total = sumBonusesBalances(
            this._balances.filter((b) => filteredEmployees.some((e) => e.id === b.employeeId))
        );
        const carry = total.reset ?? total.carry;

        return html`
            <table class="fill-horizontally">
                <thead>
                    <tr>
                        <th></th>
                        <th scope="col">Übertrag</th>
                        <th scope="col">Pauschale</th>
                        <th scope="col">Verdient</th>
                        <th scope="col">Differenz</th>
                        <th scope="col">Saldo</th>
                    </tr>
                </thead>

                <tbody class="total">
                    <tr>
                        <th scope="row"><ptc-ui-icon name="icon.feature.employees"></ptc-ui-icon> Gesamt</th>
                        <td>
                            ${typeof carry === "undefined"
                                ? html`<span class="faded">N/A</span>`
                                : html`<ptc-ui-balance .value=${carry}></ptc-ui-balance>`}
                        </td>
                        <td>
                            ${typeof total.nominal === "undefined"
                                ? html`<span class="faded">N/A</span>`
                                : formatNumber(total.nominal)}
                        </td>
                        <td>${formatNumber(total.actual)}</td>
                        <td>
                            ${typeof total.difference === "undefined"
                                ? html`<span class="faded">N/A</span>`
                                : html`
                                      <ptc-ui-balance
                                          class="semibold"
                                          iconStyle="up-down"
                                          .value=${total.difference}
                                      ></ptc-ui-balance>
                                  `}
                        </td>
                        <td>
                            ${typeof total.balance === "undefined"
                                ? html`<span class="faded">N/A</span>`
                                : html`<ptc-ui-balance class="bold" .value=${total.balance}></ptc-ui-balance>`}
                        </td>
                    </tr>
                </tbody>

                <tbody data-virtualized>
                    ${virtualize({
                        items: filteredEmployees,
                        renderItem: (employee) => {
                            const balance = this._balances.find((s) => s.employeeId === employee.id);

                            if (!balance) {
                                return html``;
                            }

                            return html`
                                <tr class="click" @click=${() => this.go(`employees/${employee.id}/bonuses-balance`)}>
                                    <th>
                                        <div class="horizontal center-aligning spacing layout employee-header">
                                            <ptc-ui-avatar
                                                class="tinier"
                                                .employee=${employee}
                                                .positionColors=${employee.positions.map((p) =>
                                                    app.company.getPositionColor(p)
                                                )}
                                            ></ptc-ui-avatar>
                                            <div class="stretch ellispis collapse">
                                                <span class="semibold">
                                                    ${getEmployeeName(
                                                        employee,
                                                        employeeNameOrderForSort(app.employeeFilters.sortProperty)
                                                    )}
                                                </span>
                                                ${employee.staffNumber
                                                    ? html` <span class="subtle">#${employee.staffNumber}</span> `
                                                    : ""}
                                            </div>
                                        </div>
                                    </th>
                                    <td
                                        ${typeof balance.reset === "number"
                                            ? popover(html`Konto zurückgesetzt am ${formatDate(balance.from)}`, {
                                                  class: "tooltip",
                                                  trigger: "hover",
                                              })
                                            : nothing}
                                    >
                                        ${typeof balance.carry === "number"
                                            ? html`<ptc-ui-balance .value=${balance.carry}></ptc-ui-balance>`
                                            : html`<span class="subtle">N/A</span>`}
                                        ${typeof balance.reset === "number"
                                            ? html`
                                                  <ptc-ui-icon
                                                      name="icon.navigation.forward"
                                                      class="smaller"
                                                  ></ptc-ui-icon>
                                                  <ptc-ui-balance class="bold" .value=${balance.reset}></ptc-ui-balance>
                                              `
                                            : ""}
                                    </td>
                                    <td>
                                        ${typeof balance.nominal === "undefined"
                                            ? html`<span class="subtle">N/A</span>`
                                            : formatNumber(balance.nominal)}
                                    </td>
                                    <td>
                                        ${balance.actual !== balance.untaxed
                                            ? html`<sup
                                                  class="tinier"
                                                  ${popover(
                                                      html`
                                                          <table class="simple">
                                                              <thead>
                                                                  <tr>
                                                                      <th style="text-align: left;">Ursache</th>
                                                                      <th>Betrag (€)</th>
                                                                  </tr>
                                                              </thead>
                                                              <tbody>
                                                                  <tr>
                                                                      <th>
                                                                          <ptc-ui-icon
                                                                              name="icon.object.bonus"
                                                                          ></ptc-ui-icon>
                                                                          Beitragsfrei
                                                                      </th>
                                                                      <td>${formatNumber(balance.untaxed)}</td>
                                                                  </tr>
                                                                  <tr>
                                                                      <th>
                                                                          <ptc-ui-icon
                                                                              name="icon.object.bonus-taxable"
                                                                          ></ptc-ui-icon>
                                                                          Beitragspflichtig
                                                                      </th>
                                                                      <td>${formatNumber(balance.taxed)}</td>
                                                                  </tr>
                                                              </tbody>
                                                          </table>
                                                      `,
                                                      {
                                                          trigger: "hover",
                                                          class: "text-left-aligning tooltip non-interactive",
                                                      }
                                                  )}
                                              >
                                                  <ptc-ui-icon name="icon.object.bonus-taxable"></ptc-ui-icon>
                                              </sup> `
                                            : ""}
                                        ${formatNumber(balance.actual)}
                                    </td>
                                    <td>
                                        ${typeof balance.difference === "undefined"
                                            ? html`<span class="subtle">N/A</span>`
                                            : html`
                                                  <ptc-ui-balance
                                                      .value=${balance.difference}
                                                      iconStyle="up-down"
                                                      class="semibold"
                                                  ></ptc-ui-balance>
                                              `}
                                    </td>
                                    <td>
                                        ${balance.subBalances
                                            ? html`<sup
                                                  class="tinier bold"
                                                  ${popover(
                                                      html`
                                                          <table class="simple">
                                                              <thead>
                                                                  <tr>
                                                                      <th></th>
                                                                      <th scope="col">Übertrag</th>
                                                                      <th scope="col">Pauschale</th>
                                                                      <th scope="col">Verdient</th>
                                                                      <th scope="col">Differenz</th>
                                                                      <th scope="col">Saldo</th>
                                                                  </tr>
                                                              </thead>

                                                              <tbody class="total">
                                                                  ${balance.subBalances.map(
                                                                      (subBalance) => html`
                                                                          <tr>
                                                                              <th>${formatRange(subBalance)}</th>
                                                                              <td
                                                                                  class="center-aligning end-justifying horizontal layout"
                                                                              >
                                                                                  ${typeof subBalance.carry === "number"
                                                                                      ? html`
                                                                                            <ptc-ui-balance
                                                                                                .value=${subBalance.carry}
                                                                                            ></ptc-ui-balance>
                                                                                        `
                                                                                      : html`<span class="subtle"
                                                                                            >N/A</span
                                                                                        >`}
                                                                                  ${typeof subBalance.reset === "number"
                                                                                      ? html`
                                                                                            <ptc-ui-icon
                                                                                                name="icon.navigation.forward"
                                                                                                class="smaller"
                                                                                            ></ptc-ui-icon>
                                                                                            <ptc-ui-balance
                                                                                                class="bold"
                                                                                                .value=${subBalance.reset}
                                                                                            ></ptc-ui-balance>
                                                                                        `
                                                                                      : ""}
                                                                              </td>
                                                                              <td>
                                                                                  ${typeof subBalance.nominal ===
                                                                                  "number"
                                                                                      ? formatNumber(subBalance.nominal)
                                                                                      : html`<span class="subtle"
                                                                                            >N/A</span
                                                                                        >`}
                                                                              </td>
                                                                              <td>
                                                                                  ${formatNumber(subBalance.actual)}
                                                                              </td>
                                                                              <td>
                                                                                  ${typeof subBalance.difference ===
                                                                                  "number"
                                                                                      ? html`
                                                                                            <ptc-ui-balance
                                                                                                .value=${subBalance.difference}
                                                                                                iconStyle="up-down"
                                                                                                class="semibold"
                                                                                            ></ptc-ui-balance>
                                                                                        `
                                                                                      : html`<span class="subtle"
                                                                                            >N/A</span
                                                                                        >`}
                                                                              </td>
                                                                              <td>
                                                                                  ${typeof subBalance.balance ===
                                                                                  "number"
                                                                                      ? html`
                                                                                            <ptc-ui-balance
                                                                                                .value=${subBalance.balance}
                                                                                                iconStyle="up-down"
                                                                                                class="semibold"
                                                                                            ></ptc-ui-balance>
                                                                                        `
                                                                                      : html`<span class="subtle"
                                                                                            >N/A</span
                                                                                        >`}
                                                                              </td>
                                                                          </tr>
                                                                      `
                                                                  )}
                                                              </tbody>
                                                          </table>
                                                      `,
                                                      {
                                                          trigger: "hover",
                                                          class: "non-interactive",
                                                      }
                                                  )}
                                              >
                                                  <ptc-ui-icon name="icon.format.bullet-list"></ptc-ui-icon>
                                              </sup> `
                                            : ""}
                                        ${typeof balance.balance === "number"
                                            ? html`
                                                  <ptc-ui-balance
                                                      .value=${balance.balance}
                                                      iconStyle="up-down"
                                                      class="semibold"
                                                  ></ptc-ui-balance>
                                              `
                                            : html`<span class="subtle">N/A</span>`}
                                    </td>
                                </tr>
                            `;
                        },
                    })}
                </tbody>
            </table>
        `;
    }

    render() {
        return html`
            <div class="center-aligning horizontal layout padded">
                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) => this.go(null, e.detail)}
                    .range=${this.dateRange}
                    maxDays=${366}
                ></ptc-date-range-picker>

                <div class="stretch end-justifying horizontal layout collapse">
                    <button
                        class="slim transparent"
                        title="Zuschlagskonten als Excel-Datei Exportieren"
                        @click="${() => {
                            this.go("exports/employee-table-excel", {
                                from: this.dateRange?.from,
                                to: this.dateRange?.to,
                                "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                columns: serializeColumOptions([
                                    { type: "fullId" },
                                    { type: "bonusesBalanceCarry" },
                                    { type: "bonusesBalanceNominal" },
                                    { type: "bonusesBalanceActual" },
                                    { type: "bonusesBalanceDifference" },
                                    { type: "bonusesBalanceBalance" },
                                ]),
                            });
                        }}"
                    >
                        <ptc-ui-icon name="icon.file.excel"></ptc-ui-icon>
                    </button>

                    ${app.hasPermission("manage.exports.ledgers_bonuses")
                        ? html`<button
                              class="transparent slim right-margined"
                              title="Zuschlagskonten Exportieren"
                              @click=${() =>
                                  this.go("exports/balances-bonuses-excel", {
                                      from: this.dateFrom,
                                      to: this.dateTo,
                                      "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                  })}
                          >
                              <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                          </button>`
                        : nothing}

                    <button class="slim transparent" title="Mehr">
                        <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                    </button>

                    <ptc-popover class="popover-menu">
                        <button @click=${this._resetBalances}>
                            <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konten Zurücksetzen
                        </button>
                        <button
                            @click=${async () => {
                                const recalculated = await this._recalculateResultsDialog.show({
                                    filters: app.employeeFilters.filters,
                                });
                                if (recalculated) {
                                    // update in the background
                                    void this.load();
                                }
                            }}
                        >
                            <ptc-ui-icon name="icon.action.recalculate"></ptc-ui-icon> Konten Neu Berechnen
                        </button>
                    </ptc-popover>
                </div>
            </div>

            <ptc-employees-filter
                class="border-top border-bottom"
                .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
            ></ptc-employees-filter>

            <div class="scroller">
                <div class="double-padded">${this._renderBalances()}</div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
